import {
  ContactCompanySearchResult,
  DoorSearchResult,
} from '../models/companies'
import { baseAPICall } from './baseAPICall'
import { clientInstance } from '../utils/http-client'

// Search API functions
export async function searchCompaniesList(
  params: any = {},
  signal?: AbortSignal
): Promise<ContactCompanySearchResult[]> {
  return baseAPICall(() =>
    clientInstance.get('contacts/companies-search/', {
      signal,
      params,
    })
  )
}

export async function searchCompaniesUniqueDomain(
  domain: string,
  signal?: AbortSignal
): Promise<ContactCompanySearchResult[]> {
  return baseAPICall(() =>
    clientInstance.get('contacts/companies-search/domain', {
      signal,
      params: { domain: domain },
    })
  )
}

export async function searchFirstBiteCompanies(
  search: string,
  options: {
    category_filter?: string
    type_filter?: string
    city_filter?: string
    state_filter?: string
    zipcode_filter?: string
    sort_by?: string
    limit?: number
    offset?: number
  } = {},
  signal?: AbortSignal
): Promise<{
  results: ContactCompanySearchResult[]
  total: number
  search_type?: string
}> {
  const params: any = { q: search }

  // Add filter parameters
  Object.keys(options).forEach((key) => {
    if (options[key as keyof typeof options] !== undefined) {
      params[key] = options[key as keyof typeof options]
    }
  })

  const response = await baseAPICall(() =>
    clientInstance.get('common/search/chains_name_and_domain/', {
      signal,
      params,
    })
  )

  // If the API returns the new format with results and total, use it
  if (response && typeof response === 'object' && 'results' in response) {
    return response as {
      results: ContactCompanySearchResult[]
      total: number
      search_type?: string
    }
  }

  // Otherwise, treat the response as just the results array (backward compatibility)
  return {
    results: Array.isArray(response) ? response : [],
    total: Array.isArray(response) ? response.length : 0,
  }
}

export async function listMyCompanies(
  options: {
    category_filter?: string
    type_filter?: string
    city_filter?: string
    state_filter?: string
    zipcode_filter?: string
    sort_by?: string
    limit?: number
    offset?: number
  } = {},
  signal?: AbortSignal
): Promise<{
  results: ContactCompanySearchResult[]
  total: number
  search_type?: string
}> {
  const params: any = {}
  Object.keys(options).forEach((key) => {
    if (options[key as keyof typeof options] !== undefined) {
      params[key] = options[key as keyof typeof options]
    }
  })

  const response = await baseAPICall(() =>
    clientInstance.get('common/search/chains_my/', {
      signal,
      params,
    })
  )

  if (response && typeof response === 'object' && 'results' in response) {
    return response as {
      results: ContactCompanySearchResult[]
      total: number
      search_type?: string
    }
  }

  return {
    results: Array.isArray(response) ? response : [],
    total: Array.isArray(response) ? response.length : 0,
  }
}

export async function searchUnmatchedCompanies(
  search: string,
  options: {
    category_filter?: string
    type_filter?: string
    city_filter?: string
    state_filter?: string
    zipcode_filter?: string
    sort_by?: string
    limit?: number
    offset?: number
  } = {},
  signal?: AbortSignal
): Promise<{
  results: ContactCompanySearchResult[]
  total: number
  search_type?: string
}> {
  const params: any = { q: search }

  // Add filter parameters
  Object.keys(options).forEach((key) => {
    if (options[key as keyof typeof options] !== undefined) {
      params[key] = options[key as keyof typeof options]
    }
  })

  const response = await baseAPICall(() =>
    clientInstance.get('common/search/chains_unmatched/', {
      signal,
      params,
    })
  )

  // If the API returns the new format with results and total, use it
  if (response && typeof response === 'object' && 'results' in response) {
    return response as {
      results: ContactCompanySearchResult[]
      total: number
      search_type?: string
    }
  }

  // Otherwise, treat the response as just the results array (backward compatibility)
  return {
    results: Array.isArray(response) ? response : [],
    total: Array.isArray(response) ? response.length : 0,
  }
}

export async function searchFirstBiteDoors(
  search: string,
  options: {
    category_filter?: string
    type_filter?: string
    city_filter?: string
    state_filter?: string
    zipcode_filter?: string
    sort_by?: string
    limit?: number
    offset?: number
  } = {},
  signal?: AbortSignal
): Promise<{
  results: DoorSearchResult[]
  total: number
  search_type?: string
}> {
  const params: any = { q: search }

  // Add filter parameters
  Object.keys(options).forEach((key) => {
    if (options[key as keyof typeof options] !== undefined) {
      params[key] = options[key as keyof typeof options]
    }
  })

  const response = await baseAPICall(() =>
    clientInstance.get('common/search/doors_name_domain_and_address/', {
      signal,
      params,
    })
  )

  // If the API returns the new format with results and total, use it
  if (response && typeof response === 'object' && 'results' in response) {
    return response as {
      results: DoorSearchResult[]
      total: number
      search_type?: string
    }
  }

  // Otherwise, treat the response as just the results array (backward compatibility)
  return {
    results: Array.isArray(response) ? response : [],
    total: Array.isArray(response) ? response.length : 0,
  }
}

export async function searchUnmatchedDoors(
  search: string,
  options: {
    category_filter?: string
    type_filter?: string
    city_filter?: string
    state_filter?: string
    zipcode_filter?: string
    sort_by?: string
    limit?: number
    offset?: number
  } = {},
  signal?: AbortSignal
): Promise<{
  results: DoorSearchResult[]
  total: number
  search_type?: string
}> {
  const params: any = { q: search }

  // Add all filter parameters
  if (options.category_filter) params.category_filter = options.category_filter
  if (options.type_filter) params.type_filter = options.type_filter
  if (options.city_filter) params.city_filter = options.city_filter
  if (options.state_filter) params.state_filter = options.state_filter
  if (options.zipcode_filter) params.zipcode_filter = options.zipcode_filter
  if (options.sort_by) params.sort_by = options.sort_by
  if (options.limit !== undefined) params.limit = options.limit
  if (options.offset !== undefined) params.offset = options.offset

  const response = await baseAPICall(() =>
    clientInstance.get('common/search/doors_unmatched/', { params, signal })
  )

  // If the API returns the new format with results and total, use it
  if (response && typeof response === 'object' && 'results' in response) {
    return response as {
      results: DoorSearchResult[]
      total: number
      search_type?: string
    }
  }

  // Otherwise, treat the response as just the results array (backward compatibility)
  return {
    results: Array.isArray(response) ? response : [],
    total: Array.isArray(response) ? response.length : 0,
  }
}

// Syndigo product data for variants
export interface SyndigoProductData {
  id: number
  syndigo_product_id: string
  brand_name: string | null
  category: string | null
  last_synced_at: string | null
}

// Variant search result interface
export interface VariantSearchResult {
  id: number
  name: string
  sku: string | null
  gtin: string | null
  format_pack_size: string | null
  avg_price: number | null
  product_id: number | null
  product_name: string | null
  match_status: 'matched' | 'unmatched' | 'ignored'
  created: string | null
  modified: string | null
  syndigo_product: SyndigoProductData | null
}

export async function searchVariants(
  search: string,
  options: {
    limit?: number
    offset?: number
    product_id?: number
  } = {},
  signal?: AbortSignal
): Promise<{
  results: VariantSearchResult[]
  total: number
  search_type?: string
}> {
  const params: Record<string, string | number> = { q: search }

  // Add pagination parameters
  if (options.limit !== undefined) params.limit = options.limit
  if (options.offset !== undefined) params.offset = options.offset
  // Add product filter
  if (options.product_id !== undefined) params.product_id = options.product_id

  const response = await baseAPICall(() =>
    clientInstance.get('common/search/variants/', { params, signal })
  )

  // If the API returns the new format with results and total, use it
  if (response && typeof response === 'object' && 'results' in response) {
    return response as {
      results: VariantSearchResult[]
      total: number
      search_type?: string
    }
  }

  // Otherwise, treat the response as just the results array (backward compatibility)
  return {
    results: Array.isArray(response) ? response : [],
    total: Array.isArray(response) ? response.length : 0,
  }
}
